import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import EntityAgreementAPIService from "legal-entities/agreements/_api/entity-agreement-api-service";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import _ from "lodash";
import Session from "users/__session/session";

type EntityAgreementStoreState = {
  byId: {
    entries: Record<string, EntityAgreementJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: EntityAgreementStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
};

export const populateEntityAgreement = createAsyncThunk(
  "entityAgreements/getEntityAgreementById",
  async ({ session, id }: { session: Session; id: Guid }, thunkAPI) => {
    const apiService = new EntityAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getEntityAgreementById(id, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const populateEntityAgreements = createAsyncThunk(
  "entityAgreements/getEntityAgreementsByIds",
  async ({ session, entityAgreementIds }: { session: Session; entityAgreementIds: Guid[] }, thunkAPI) => {
    if (!entityAgreementIds.length) return [];

    const store: { entityAgreements: EntityAgreementStoreState } = thunkAPI.getState() as {
      entityAgreements: EntityAgreementStoreState;
    };
    const missingEntityAgreementIds = _.uniqWith(
      entityAgreementIds.filter((entityAgreementId) => !store.entityAgreements.byId.entries[entityAgreementId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingEntityAgreementIds.length) return [];

    const apiService = new EntityAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getEntityAgreementsByIds(missingEntityAgreementIds, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const entityAgreementsSlice = createSlice({
  name: "entityAgreements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(populateEntityAgreement.pending, (state, action) => {
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });
    builder.addCase(populateEntityAgreement.fulfilled, (state, action) => {
      const id = action.meta.arg.id.value;
      state.byId.entries[id] = action.payload.toJSON();
      state.byId.loading[id] = false;
    });
    builder.addCase(populateEntityAgreement.rejected, (state, action) => {
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = false;
      state.byId.error[id] = action.payload as SerializedError;
    });
    builder.addCase(populateEntityAgreements.pending, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) return;

      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = true;
        state.byId.error[entityAgreementId.value] = null;
      }
    });
    builder.addCase(populateEntityAgreements.fulfilled, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) return;

      for (const entityAgreement of action.payload) {
        state.byId.entries[entityAgreement.id.value] = entityAgreement.toJSON();
      }

      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
      }
    });
    builder.addCase(populateEntityAgreements.rejected, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) {
        return;
      }
      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
        state.byId.error[entityAgreementId.value] = action.error;
      }
    });
  },
});

export const getEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return;
  const json = state.legalEntityAgreements?.byId.entries[id.value];
  return EntityAgreement.fromJSON(json);
};
export const getIsLoadingEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return false;
  return state.legalEntityAgreements?.byId.loading[id.value];
};

export const getErrorLoadingEntityAgreementById = (id?: Guid) => (state: RootState) => {
  if (!id) return undefined;
  return state.legalEntityAgreements?.byId.error[id.value];
};
const selectEntityAgreementsByIds = createSelector(
  [(state: RootState) => state.legalEntityAgreements.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => EntityAgreement.fromJSON(entries[id.value]))
      .filter((entityAgreement) => entityAgreement !== undefined);
  }
);
export const getEntityAgreementsByIds = (ids: Guid[]) => (state: RootState) => selectEntityAgreementsByIds(state, ids);

const selectLoadingEntityAgreementIds = createSelector(
  [(state: RootState) => state.legalEntityAgreements.byId.loading],
  (loadingMap) => {
    return Object.keys(loadingMap)
      .filter((id) => loadingMap[id])
      .map((id) => Guid.fromJSON(id));
  }
);
export const getLoadingEntityAgreementIds = (state: RootState) => 
  selectLoadingEntityAgreementIds(state);
export const getIsLoadingEntityAgreementByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.legalEntityAgreements.byId.loading[id.value]);
export const getAreErrorsLoadingEntityAgreementsByIds = (ids: Guid[]) => (state: RootState) =>
  ids.some((id) => state.legalEntityAgreements.byId.error[id.value]);
export type EntityAgreementError = {
  id: Guid;
  error: SerializedError;
};

const selectAllEntityAgreementErrors = createSelector(
  [(state: RootState) => state.legalEntityAgreements],
  (entityAgreements) => {
    return Object.keys(entityAgreements)
      .filter((entityAgreementId) => entityAgreementId && entityAgreements.byId.error[entityAgreementId])
      .map((id) => {
        const error = entityAgreements.byId.error[id]?.message;
        if (!error) return null;
        const entityAgreementId = Guid.fromJSON(id);
        if (!entityAgreementId) return null;
        return { id: entityAgreementId, error };
      })
      .filter((record) => record?.id && record.error) as EntityAgreementError[];
  }
);
export const getAllEntityAgreementErrors = (state: RootState) =>
  selectAllEntityAgreementErrors(state);